/*
 * Models for content groups (folders) and collections of content
 */

define([
  "backbone",
  "messaging/messenger",
  "configuration/properties",
  "jquery",
  "modules/contentsSplit/content/contentModel",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, Messenger, Properties, $, ContentModel, SortableListModel) {
  var ContentsModel = {};

  ContentsModel.contentGroup = Backbone.Model.extend({
    idAttribute: "groupId",
    defaults: {
      groupName: "",
      contentAwarenessOn: false,
    },
    defaultValidateAttributes: ["groupName"],
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/mappings/groups/" + this.id;
    },
    addContent: function () {
      var newContent = new ContentModel.content();
      var groupUrl = this.get("mappings").href;
      //set content url
      newContent.url = function () {
        return groupUrl;
      };
      if (this.contentsCol) {
        this.contentsCol.add(newContent);
      }
      return newContent;
    },
    addSubGroup: function () {
      var newGroup = new ContentsModel.contentGroup();
      this.subGroups().add(newGroup);
      this.subGroups().sort();
      return newGroup;
    },
    //move an array of content from the current folder to the destination folder
    move: function (contentToMove, destinationGroup) {
      var length = contentToMove.length;
      for (var i = 0; i < length; i++) {
        var content = contentToMove[i];
        //remove the content from the group's collection (use content.collection instead of this.contents() because we might be dealing with a modfied/missing/ignored collection of content)
        content.collection.remove(content);
        //move each content (sends an update to the server)
        content.move(destinationGroup.get("groupId"));
        //now if the destination folder has its contents loaded already, make sure to move the
        //content to that collection
        if (destinationGroup.contentsCol) {
          destinationGroup.contentsCol.add(content);
        }
      }
    },
    //move this folder to another folder.  Assumes that this folder has already been removed from whatever
    //collection it was part of
    moveFolder: function (destinationGroup) {
      this.set("parentId", destinationGroup.get("groupId"));
      //when the destination group's sub groups are synced, add this group
      destinationGroup.subGroups().onSynced(function () {
        destinationGroup.subGroups().add(this);
        //make sure that there is no naming conflict in the new collection
        //add a (1),(2),(3)... to the end of the name if there is a conflict
        var value = this.get("groupName");
        var originalValue = value;
        var number = 1;
        while (this.validateAttribute("groupName", value)) {
          value = originalValue + "(" + number + ")";
          number++;
        }
        this.set("groupName", value, { silent: true });
        this.trigger("enter"); //trigger enter event as if the name change was made by a textbox
        destinationGroup.trigger("expand"); //make sure we see the moved folder in the parent folder
        this.trigger("select"); //make sure the current folder is still selected
      }, this);
    },
    parse: function (response) {
      //if the group is the top level group, then replace the name of the group with the i18n'ed version
      if (response.groupId === 0) {
        response.groupName = $.t("contents.title");
      }
      return response;
    },
    saveChildren: function (options) {
      //set the parentId of the children
      if (this.subGroupsCol) {
        var parentId = this.get("groupId");
        var url = this.get("subgroups").href;
        this.subGroupsCol.each(function (group) {
          group.set("parentId", parentId);
          group.url = url; //pass url to children
        });
      }
      this.saveSubModels(options);
    },
    subGroups: function () {
      this.subGroupsCol = this.getSubModel(
        this.subGroupsCol,
        ContentsModel.contentGroups,
        "/subgroups",
        true,
        !this.get("hasSubgroups"),
      );
      //pass the parent object to the collection for reference
      this.subGroupsCol.parent = this;
      //add events to determine when to change the hasSubgroups attribute
      this.subGroupsCol.on(
        "add remove sync",
        function () {
          var wasDirty = this.dirty;
          this.set("hasSubgroups", this.subGroupsCol.length > 0);
          this.dirty = wasDirty; //do not let the set event change the dirty flag because changing hasSubgroups should not require a save (but we do need to fire a change event)
        },
        this,
      );
      return this.subGroupsCol;
    },
    contents: function () {
      //We must pass this.contentsCol as the first parameter or else moving multiple items later on
      //won't work correctly.
      //Save the dirty state.  We don't want switching this view to trigger a save
      var wasDirty = this.dirty;
      this.set("contentAwarenessOn", false);
      this.dirty = wasDirty;

      //load content models
      if (!this.contentsCol) {
        this.contentsCol = new ContentsModel.contents();
        //if the group exists on the server, load the content from the server
        if (!this.isNew()) {
          var url = this.get("mappings").href;
          this.contentsCol.url = url;
          this.contentsCol.fetch();
        }
      }
      return this.contentsCol;
    },
    modifiedContent: function () {
      //This fetches modified content within a group and its subgroups
      var wasDirty = this.dirty;
      this.set("contentAwarenessOn", true);
      this.dirty = wasDirty;
      //fetch the collection if it doesn't already exist
      if (!this.modifiedContentCol) {
        this.modifiedContentCol = new ContentsModel.contents();
        //set modifiedCol parameter to true to signify that this collection is only for modified content
        this.modifiedContentCol.modifiedCol = true;
        if (!this.isNew()) {
          var url = _.result(this, "url") + "/modified";
          this.modifiedContentCol.url = url;
          this.modifiedContentCol.fetch();
        }
      }
      return this.modifiedContentCol;
    },
    missingContent: function () {
      this.clearContentCollections();
      //This fetches missing content within a group and its subgroups
      var wasDirty = this.dirty;
      this.set("contentAwarenessOn", true);
      this.dirty = wasDirty;
      //fetch the collection if it doesn't already exist
      if (!this.missingContentCol) {
        this.missingContentCol = new ContentsModel.contents();
        if (!this.isNew()) {
          var url = _.result(this, "url") + "/missing";
          this.missingContentCol.url = url;
          this.missingContentCol.fetch();
        }
      }
      return this.missingContentCol;
    },
    ignoredContent: function () {
      //This fetches ignored content within a group and its subgroups
      var wasDirty = this.dirty;
      this.set("contentAwarenessOn", true);
      this.dirty = wasDirty;
      //fetch the collection if it doesn't already exist
      if (!this.ignoredContentCol) {
        this.ignoredContentCol = new ContentsModel.contents();
        //set ignoredCol parameter to true to signify that this collection is only for ignored content
        this.ignoredContentCol.ignoredCol = true;
        if (!this.isNew()) {
          var url = _.result(this, "url") + "/ignored";
          this.ignoredContentCol.url = url;
          this.ignoredContentCol.fetch();
        }
      }
      return this.ignoredContentCol;
    },
    newContent: function () {
      //This fetches new content within a group and its subgroups
      var wasDirty = this.dirty;
      this.set("contentAwarenessOn", true);
      this.dirty = wasDirty;
      //fetch the collection if it doesn't already exist
      if (!this.newContentCol) {
        this.newContentCol = new ContentsModel.contents();
        if (!this.isNew()) {
          var url = _.result(this, "url") + "/new";
          this.newContentCol.url = url;
          this.newContentCol.fetch();
        }
      }
      return this.newContentCol;
    },
    newContentEnabled: function () {
      //This returns whether New Content is enabled for Content Awareness
      //For 7.0, this should be false.
      //For 7.1, this will be true.
      //We may wish to store this in the DB rather than just in JS
      return false;
    },
    clearContentCollections: function () {
      //this function recursively descends the nav tree (as much as it has been loaded) and
      //clears the contents collection, as well as the missing/modified/ignored/new collections.  This basically causes all content collections
      //to be fetched fresh from the server
      delete this.contentsCol;
      delete this.missingContentCol;
      delete this.modifiedContentCol;
      delete this.ignoredContentCol;
      delete this.newContentCol;
      if (this.subGroupsCol) {
        this.subGroupsCol.each(function (group) {
          group.clearContentCollections();
        }, this);
      }
    },
    _validateDuplicateGroupName: function (value) {
      if (this.collection) {
        for (let i = 0; i < this.collection.length; i++) {
          const model = this.collection.at(i);
          if (model !== this) {
            if (value.toUpperCase() === model.get("groupName").toUpperCase()) {
              return $.t("contents.controls.duplicateValidationError");
            }
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "groupName") {
        return (
          this.validateRequiredValue(value, { maxLength: 255 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateGroupName(value)
        );
      }
    },
  });

  ContentsModel.contentGroups = Backbone.Collection.extend({
    model: ContentsModel.contentGroup,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "groupName");
    },
  });

  ContentsModel.contents = Backbone.Collection.extend({
    model: ContentModel.content,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },
  });

  //Request to load the top level content group
  Messenger.reqres.setHandler("root:content:group", function () {
    var group = new ContentsModel.contentGroup({ groupId: 0 });
    group.fetch();
    return group;
  });

  //Request to load the counts of missing/modified/new content urls
  Messenger.reqres.setHandler(
    "content:awareness:count",
    function (localeId) {
      var counts = new Backbone.Model({
        newCount: -1,
        missingCount: -1,
        modifiedCount: -1,
      });
      var kbId = Messenger.reqres.request("active:kb");
      counts.url =
        Properties.rootURL + "/" + kbId + "/mappings/locale/" + localeId + "/awareness/count";
      counts.fetch();
      return counts;
    },
    this,
  );

  //Request for the headers of the contents list
  Messenger.reqres.setHandler("contents:headers", function () {
    var Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({ checkbox: true, width: "14px" }),
      new Header({
        headerName: $.t("contents.headers.name"),
        modelAttribute: "name",
        selectable: true,
        sortable: true,
        filterable: true,
        longTextField: true,
      }),
    ]);
  });

  //Request for the headers of the contents list for New Content
  Messenger.reqres.setHandler("contents:headers:newContent", function () {
    var Header = SortableListModel.headerModel;
    Messenger.vent.on(
      "add:content:new",
      function (someUrl) {
        alert("got request to add content with url " + someUrl);
      },
      this,
    );
    var customHtml1 = '<i class="icon-play-circle icon-2x astute-blue" >';
    var customHtml = '<i class="icon-umbrella icon-2x astute-blue addFromNewContent" >';
    return new Backbone.Collection([
      //new Header({checkbox: true, width: "14px"}),
      new Header({
        headerName: $.t("contents.headers.url"),
        modelAttribute: "urlValue",
        selectable: true,
        sortable: true,
        textField: true,
      }),
      new Header({
        headerName: $.t("contents.headers.time"),
        modelAttribute: "timestamp",
        selectable: true,
        sortable: true,
        textField: true,
      }),
      new Header({
        headerName: "",
        selectable: true,
        sortable: false,
        textField: true,
        customHtml: customHtml1,
      }),
      new Header({
        headerName: "",
        selectable: true,
        sortable: false,
        textField: true,
        customHtml: customHtml,
      }),
    ]);
  });

  return ContentsModel;
});
